import { initialState } from './ai-session-context-plugin';
import type { SessionContextAction, SessionContextPluginState } from './types';

/**
 * Reduces AI experience context actions to update the plugin state.
 * Handles actions for resetting, prompt submission, streaming, completion, and errors.
 *
 * @param state - The current AI experience context plugin state
 * @param action - The action to process
 * @returns The updated AI experience context plugin state
 */
export function reducer({
	state,
	action,
}: {
	action: SessionContextAction;
	state: SessionContextPluginState;
}): SessionContextPluginState {
	switch (action.type) {
		case 'RESET':
			return initialState;
		case 'START_SESSION':
			return {
				...state,
				sessionId: action.sessionId,
				status: 'streaming',
			};
		case 'PROMPT':
			return {
				...state,
				status: 'prompt',
			};
		case 'STREAM':
			// Prevent streaming if the session is in an error state
			if (state.status === 'error') {
				return {
					...state,
				};
			}
			// if the previous state was complete it means we are starting a new stream
			const responseVersion = state.status === 'complete' ? 0 : state.responseVersion + 1;
			return {
				...state,
				adfResponse: action.adfResponse,
				markdown: action.markdown,
				status: 'streaming',
				responseVersion,
			};
		case 'COMPLETE':
			// Prevent completing the session if it is in an error state
			if (state.status === 'error') {
				return {
					...state,
				};
			}
			return {
				...state,
				adfResponse: action.adfResponse,
				markdown: action.markdown,
				responseTooSimilar: action.responseTooSimilar,
				status: 'complete',
				// If this completion is for a queue-timeout force-finished tool call,
				// remember its id so the next `buildCommitTransaction` persists its
				// positions for resume.
				pendingForceAbortedToolCallId: action.forceAbortedToolCallId,
			};
		case 'ERROR':
			return {
				...state,
				errorInfo: action.errorInfo,
				status: 'error',
			};
		case 'UPDATE_TRACE_IDS':
			return {
				...state,
				traceIds: action.traceIds,
			};
		case 'UPDATE_ID_MAP':
			return {
				...state,
				idMap: { ...state.idMap, ...action.idMap },
			};
		case 'CANCEL_STREAM':
			// Set the cancelRequested flag so the plugin's view.update() can abort the network request.
			// The subsequent RESET action will clear this flag along with the rest of the state.
			return {
				...state,
				cancelRequested: true,
			};
		case 'PAUSE_STREAM':
			return {
				...state,
				streamPaused: true,
			};
		case 'RESUME_STREAM':
			return {
				...state,
				streamPaused: false,
			};
		case 'SET_HAS_USER_SCROLLED':
			return {
				...state,
				hasUserScrolled: true,
			};
		case 'START_FREE_GEN_STREAM':
			return {
				...state,
				status: 'streaming',
				userInput: action.userInput,
			};
		// [CCI-15904] Ends the AI session but retains aiContentPositions so the
		// Post Stream Review toolbar can navigate them. Resets ephemeral stream
		// state (sessionId, status, adfResponse) but NOT the positions array.
		//
		// (afm-ai-pr-review-bot #8) Also clear `currentStreamReviewSegmentIndex`
		// and `currentStreamReviewAiOptIn` to defend against a brand-new AI
		// session starting before the user closes the post-stream-review
		// toolbar (which would otherwise hit `RESET`). Without clearing,
		// `buildCommitTransaction` could append fresh entries into a new
		// `aiContentPositions` array while the stale index from the previous
		// session points out-of-bounds into the shorter new array.
		case 'END_SESSION':
			return {
				...state,
				sessionId: undefined,
				status: 'idle',
				adfResponse: undefined,
				markdown: undefined,
				cancelRequested: false,
				currentStreamReviewSegmentIndex: undefined,
				currentStreamReviewAiOptIn: undefined,
				// Drop any fine step-through segments from a prior review so
				// a fresh session's toolbar can't index into a stale array.
				streamReviewSegments: undefined,
				// Clear here too, in case buildCommitTransaction returned early without consuming it.
				pendingForceAbortedToolCallId: undefined,
			};
		case 'POST_STREAM_REVIEW': {
			let next: SessionContextPluginState = {
				...state,
				status: 'postStreamReview',
				currentStreamReviewSegmentIndex: action.currentStreamReviewSegmentIndex,
				// Only overwrite the captured aiOptIn when the action provides one — keeps
				// the originally-captured value across subsequent UPDATE_STREAM_REVIEW_SEGMENT
				// re-opens (which are also dispatched as POST_STREAM_REVIEW from openAIModal).
				currentStreamReviewAiOptIn: action.aiOptIn ?? state.currentStreamReviewAiOptIn,
			};
			// Apply the optional Undo/Redo `isUndone` flip in the SAME pass, so the
			// modal open and the state change are atomic (no follow-up dispatch):
			// `scope: 'all'` flips every aggregate entry, `scope: 'segment'` flips one
			// fine step-through segment.
			if (action.undoneUpdate) {
				const { undoneUpdate } = action;
				if (undoneUpdate.scope === 'all') {
					next = {
						...next,
						aiContentPositions: (next.aiContentPositions ?? []).map((entry) => ({
							...entry,
							isUndone: undoneUpdate.isUndone,
						})),
					};
				} else {
					next = {
						...next,
						streamReviewSegments: (next.streamReviewSegments ?? []).map((entry, i) =>
							i === undoneUpdate.index ? { ...entry, isUndone: undoneUpdate.isUndone } : entry,
						),
					};
				}
			}
			return next;
		}
		case 'UPDATE_STREAM_REVIEW_SEGMENT':
			return {
				...state,
				currentStreamReviewSegmentIndex: action.currentStreamReviewSegmentIndex,
			};
		case 'UPDATE_AI_CONTENT_POSITION': {
			const next = (state.aiContentPositions ?? []).map((entry, i) =>
				i === action.index ? { ...entry, startPos: action.startPos, endPos: action.endPos } : entry,
			);
			return {
				...state,
				aiContentPositions: next,
			};
		}
		// Remove a stored aborted tool call once it has been resumed.
		case 'CLEAR_ABORTED_TOOL_CALL_POSITION': {
			if (!state.abortedToolCallPositions?.[action.toolCallId]) {
				return state;
			}
			const { [action.toolCallId]: _removed, ...rest } = state.abortedToolCallPositions;
			return {
				...state,
				abortedToolCallPositions: rest,
			};
		}
		// Replace the fine step-through segment list (populate on
		// entering step-through, `[]` to clear on returning to aggregate).
		case 'SET_STREAM_REVIEW_SEGMENTS':
			return {
				...state,
				streamReviewSegments: action.segments,
			};
		default:
			return state;
	}
}
